import { useLocalStorage } from '@vueuse/core'
import { ref, type Ref, onMounted } from 'vue'
import {
  netClient,
  type CommonResponse,
  OBITUARY_DELETE,
  OBITUARY_CREATE_PREVIEW,
  OBITUARY_PUBLISH,
  OBITUARY_STYLES,
  OBITUARY_PREVIEW,
  ALBUM_PATH,
  OBITUARY_DETAILS,
  OBITUARY_CREATE_WREATH_ORDER_NATIVE,
  OBITUARY_CREATE_WREATH_ORDER_JS,
  OBITUARY_CREATE_COUPLET_ORDER_NATIVE,
  OBITUARY_CREATE_COUPLET_ORDER_JS,
  OBITUARY_CHECK_NATIVE_ORDER,
  OBITUARY_LIST_WREATH_SENT,
  OBITUARY_LIST_COUPLET_SENT,
  REPORT_PV,
  OBITUARY_WREATH_THANKS,
  OBITUARY_COUPLET_THANKS,
  OBITUARY_THANKS_LIST_BY_ItemID,
  OBITUARY_THANKS_LIST_BY_THANKSID,
  OBITUARY_LIST,
  OBITUARY_WREATHS,
  OBITUARY_COUPLETS,
  OBITUARY_PREVIEW_COUPLET,
  OBITUARY_DELETE_WREATH,
  OBITUARY_DELETE_COUPLET,
  OBITUARY_SET_LIFE_PRIVILEGE,
  OBITUARY_CREATE_LIFE,
  TEST_CHECK_SUBSCRIBE_ALL,
  OBITUARY_OPEN_LIST,
  OBITUARY_CREATE_ORDER_NATIVE,
  OBITUARY_CREATE_ORDER_JS,
  MEDIA_UPLOAD_IMAGE_BY_URL,
  OBITUARY_PAY_BY_CREDIT,
  SET_MEMBER_PAID,
  OBITUAYR_OPEN
} from '@/apis/api'
import type { CreateWechatOrderResponse, CreateOrderNativeResponse, CheckNativePaidDTO } from '../payment'
import type { FileDTO } from '@/composables/media/media'
import type { UserMinDTO } from '../user/users'
import { useUserLocalStorage } from '../user/users'
import type { LifeDTO } from '../life/life'
import { handleErr } from '@/composables/exception/handler'
import { CODE_OK } from '@/apis/code'
import { navigateTo } from '#app'
import { formatDateToYYYYMMDD } from '@/utils/time'
import { inMiniprograme, isIOS } from '@/utils/util'

// 储存创建讣告时，当前讣告的所有信息，作为创建讣告草稿的请求体
export const GenerateObiMsgCommand: Ref<GenerateObiMsgCommand> = useUserLocalStorage('GenerateObiMsgCommand', () => {
  return {
    ai: true,
    fullName: '',
    birthday: '',
    deathTime: '',
    age: null,
    relationKey: '',
    relationName: '',
    deathPlace: '',
    deathReason: '',
    ceremony: {
      type: '',
      time: null,
      location: {
        latitude: 0,
        longitude: 0,
        address: '',
        name: ''
      },
      details: ''
    }
  }
})

export const autoEditForm = async (obituaryId: string) => {
  const { data, error, statusCode } = await getObituaryDetails(obituaryId)
  const resData = data as Ref<CommonResponse<ObituaryDetailsResponse>>
  if (error.value) {
    handleErr(resData, error, statusCode)
    return
  } else {
    if (resData.value.code === CODE_OK) {
      const obituary = resData.value.data.obituary
      GenerateObiMsgCommandEdit.value.ai = true
      GenerateObiMsgCommandEdit.value.fullName = obituary.fullName
      GenerateObiMsgCommandEdit.value.birthday = obituary.birthday
        ? formatDateToYYYYMMDD(new Date(obituary.birthday))
        : null
      GenerateObiMsgCommandEdit.value.deathTime = obituary.deathTime
        ? new Date(obituary.deathTime).toISOString().replace('T', ' ').substring(0, 19)
        : null
      GenerateObiMsgCommandEdit.value.age = obituary.age
      GenerateObiMsgCommandEdit.value.relationKey = obituary.relationKey
      GenerateObiMsgCommandEdit.value.relationName = obituary.relationName
      GenerateObiMsgCommandEdit.value.deathPlace = obituary.deathPlace
      GenerateObiMsgCommandEdit.value.deathReason = obituary.deathReason
      GenerateObiMsgCommandEdit.value.ceremony = obituary.ceremony

      obituaryPreviewEdit.value.base = obituary.basicInfo
      obituaryPreviewEdit.value.desc = obituary.memorialMsg
      obituaryPreviewEdit.value.ceremony = obituary.ceremonyInfo
      obituaryPreviewEdit.value.addition = obituary.additionMsg
      for (let i = 0; i < obituary.familyMembers.length; i++) {
        familyMemberEdit.value[i] = {
          name: obituary.familyMembers[i].name
        }
      }
      userOptionsEdit.value.open = obituary.open
      userOptionsEdit.value.createMemorial = obituary.createMemorial
      userOptionsEdit.value.withQrCode = obituary.withQrCode
      endPhraseIdxEdit.value.number = obituary.endPhraseIdx
      mapFileIdEdit.value.id = obituary.mapFileId
      noticeDateEdit.value.date = obituary.noticeDate ? formatDateToYYYYMMDD(new Date(obituary.noticeDate)) : null
      navigateTo(
        '/createObituary/edit?isEdit=true&obituaryId=' +
          obituary.id +
          (obituary.lifeId ? `&lifeId=${obituary.lifeId}&serviceId=${obituary.serviceId}` : '')
      )
    } else {
    }
  }
}

export const GenerateObiMsgCommandEdit: Ref<GenerateObiMsgCommand> = useUserLocalStorage(
  'GenerateObiMsgCommandEdit',
  () => {
    return {
      ai: true,
      fullName: '',
      birthday: '',
      deathTime: '',
      age: null,
      relationKey: '',
      relationName: '',
      deathPlace: '',
      deathReason: '',
      ceremony: {
        type: '',
        time: null,
        location: {
          latitude: 0,
          longitude: 0,
          address: '',
          name: ''
        },
        details: ''
      }
    }
  }
)

export interface GenerateObiMsgCommand {
  ai: boolean
  fullName: string
  birthday: string | null
  deathTime: string | null
  age: number | null
  relationKey: string
  relationName: string
  deathPlace: string
  deathReason: string
  ceremony: Ceremony
}

//
export interface Ceremony {
  type: string //  葬礼 追悼会 遗体告别仪式 追思会
  time: string | null
  location: Location
  details: string
}

export interface Location {
  latitude: number | null
  longitude: number | null
  address: string
  name: string
}

export interface FamilyMember {
  name: string
}

// response
export interface AiObituaryResult {
  base: string
  desc: string
  ceremony: string
  addition: string
}

export function createObituaryPreview(GenerateObiMsgCommand: GenerateObiMsgCommand) {
  return netClient.request<CommonResponse<AiObituaryResult>>(OBITUARY_CREATE_PREVIEW, 'post', GenerateObiMsgCommand)
}

export interface obituaryStylesResult {
  styles: obituaryStyles[]
}

export interface obituaryStyles {
  id: string
  coverFile: FileDTO
  coverId: string
  backgroundColor: string
  contentFont: string
  outdated: boolean
  rank: number
  textColor: string
  titleFont: string
  name: string
}
export function getobituaryStyles() {
  return netClient.request<CommonResponse<obituaryStylesResult>>(OBITUARY_STYLES, 'get', {
    iosMiniapp: isIOS() && inMiniprograme.value
  })
}

export const obituaryPreview = useUserLocalStorage<AiObituaryResult>('obituaryPreview', () => {
  return {
    base: '',
    desc: '',
    ceremony: '',
    addition: ''
  }
})

export const obituaryPreviewEdit = useUserLocalStorage<AiObituaryResult>('obituaryPreviewEdit', () => {
  return {
    base: '',
    desc: '',
    ceremony: '',
    addition: ''
  }
})
export const endPhraseIdx = useUserLocalStorage<any>('endPhraseIdx', () => {
  return {
    number: 0
  }
})

export const endPhraseIdxEdit = useUserLocalStorage<any>('endPhraseIdxEdit', () => {
  return {
    number: 0
  }
})

export const noticeDate = useUserLocalStorage<any>('noticeDate', () => {
  return {
    date: ''
  }
})

export const noticeDateEdit = useUserLocalStorage<any>('noticeDateEdit', () => {
  return {
    date: ''
  }
})

export const mapFileId = useUserLocalStorage<any>('mapFileId', () => {
  return {
    id: ''
  }
})

export const mapFileIdEdit = useUserLocalStorage<any>('mapFileIdEdit', () => {
  return {
    id: ''
  }
})

export const familyMember = useUserLocalStorage<FamilyMember[]>('FamilyMember', () => {
  // Check if we're on the client side before accessing localStorage
  const lang = typeof window !== 'undefined' ? localStorage.getItem('lang') : null
  const defaultName = lang === 'en' || lang === 'en_US' ? '' : '孝子 xxx 孝媳 xxx'
  return [{ name: defaultName }]
})

export const familyMemberEdit = useUserLocalStorage<FamilyMember[]>('FamilyMemberEdit', () => {
  return [{ name: '' }]
})

export interface PreviewObiCommand {
  templateId: string
  fullName: string
  basicInfo: string
  memorialMsg: string
  ceremonyInfo: string
  additionMsg: string
  familyMembers: Array<FamilyMember>
  endPhraseIdx: number // 上面文案的下标 0-5
  noticeDate: string
}

export interface PreviewObiEditCommand {
  obituaryId: string
  templateId: string
  fullName: string
  basicInfo: string
  memorialMsg: string
  ceremonyInfo: string
  additionMsg: string
  familyMembers: Array<FamilyMember>
  endPhraseIdx: number // 上面文案的下标 0-5
  noticeDate: string
}

// response
export interface ObiPreviewDTO {
  templateId: string
  imageUrl: string
}

export function getObituaryPreview(previewObiCommand: PreviewObiCommand | PreviewObiEditCommand) {
  return netClient.request<CommonResponse<ObiPreviewDTO>>(OBITUARY_PREVIEW, 'post', previewObiCommand)
}

export interface WreathTemplateResponse {
  wreathTemplateList: Array<WreathTemplateDTO>
}

export interface WreathTemplateDTO {
  id: string
  imageId: string
  image: FileDTO
  upperCouplet: string
  lowerCouplet: string
  creatorId: string
  price: string // 价格元
  to: string
  rank: number
  outdated: boolean
}

export const wreathStyleListApi = () => {
  return netClient.request<CommonResponse<WreathTemplateResponse>>(OBITUARY_WREATHS, 'get', {
    iosMiniapp: isIOS() && inMiniprograme.value
  })
}

export interface CoupletTemplateResponse {
  categories: Array<String>
  coupletTemplateList: Array<CoupletTemplateDTO>
}

export interface CoupletTemplateDTO {
  id: string
  imageId: string
  image: FileDTO
  upper: string
  lower: string
  font: string
  toCategory: string
  creatorId: string
  updatorId: string
  outdated: Boolean
  rank: number
  price: string // 加了价格
}

export const coupletStyleListApi = () => {
  return netClient.request<CommonResponse<CoupletTemplateResponse>>(OBITUARY_COUPLETS, 'get', {
    iosMiniapp: isIOS() && inMiniprograme.value
  })
}

export interface PreviewCustomCoupletCommand {
  upper: string
  lower: string
  font: string
}

export const previewCustomCouplet = (previewCommand: PreviewCustomCoupletCommand) => {
  return netClient.request<CommonResponse<string>>(OBITUARY_PREVIEW_COUPLET, 'post', previewCommand)
}

export const userOptions = useUserLocalStorage<{
  open: boolean
  createMemorial: boolean
  withQrCode: boolean
}>('userOptions', () => {
  return {
    open: true,
    createMemorial: true,
    withQrCode: false
  }
})

export const userOptionsEdit = useUserLocalStorage<{
  open: boolean
  createMemorial: boolean
  withQrCode: boolean
}>('userOptionsEdit', () => {
  return {
    open: true,
    createMemorial: true,
    withQrCode: false
  }
})

//讣告发布
export interface PublishObituaryCommand {
  obituaryId: string | null // 如果是新建，就传null，如果是编辑，就传有效id
  lifeId: string
  serviceId: string
  fullName: string
  birthday: string
  deathTime: string
  age: number
  relationKey: string
  relationName: string
  deathPlace: string
  deathReason: string
  ceremony: Ceremony // 仪式
  basicInfo: string // ↓四个输入框文案
  memorialMsg: string
  ceremonyInfo: string
  additionMsg: string
  familyMembers: Array<FamilyMember>
  withQrCode: boolean // ↓配置项
  open: boolean
  createMemorial: boolean
  styleId: string // 样式模板id
  imageUrl: string // 预览图片url
  endPhraseIdx: number // 上面文案的下标 0-5
  noticeDate: string
  mapFileId: string // 地图图片id
}

export interface ObituaryPayByCreditCommand {
  obituaryId: string
}

export function obituaryPayByCredit(payCommand: ObituaryPayByCreditCommand) {
  return netClient.request<CommonResponse<any>>(OBITUARY_PAY_BY_CREDIT, 'post', payCommand)
}

export function ObituaryPublish(PublishObituaryCommand: PublishObituaryCommand) {
  return netClient.request<CommonResponse<string>>(OBITUARY_PUBLISH, 'post', PublishObituaryCommand)
}

//讣告详情
export interface ObituaryDetailsResponse {
  obituary: ObituaryDTO // 讣告
  wreaths: Array<WreathSentDTO> // 赠送的花圈第一页
  couplets: Array<CoupletSentDTO> // 第一页挽联
  life: LifeDTO | null // 关联的人生一页，可能为null
  thanks: Array<ObituaryThanksDTO> // 被答谢消息
  meSubscribed: boolean // 是否订阅了服务号
  price: number // 价格
  obituaryUsage: number
}

export function getObituaryDetails(obituaryId: string, pageSize: number = 15) {
  return netClient.request<CommonResponse<ObituaryDetailsResponse>>(
    OBITUARY_DETAILS.replace('{id}', obituaryId),
    'get',
    {
      pageSize: pageSize,
      iosMiniapp: isIOS() && inMiniprograme.value
    }
  )
}

export function deleteObituary(obituaryId: string) {
  return netClient.request<CommonResponse<string>>(OBITUARY_DELETE.replace('{id}', obituaryId), 'delete', {})
}

// 讣告详情
export interface ObituaryDTO {
  id: string
  lifeId: string
  serviceId: string
  fullName: string
  birthday: number
  deathTime: number
  age: number
  relationKey: string
  relationName: string
  deathPlace: string
  deathReason: string
  ceremony: Ceremony
  basicInfo: string
  memorialMsg: string
  ceremonyInfo: string
  additionMsg: string
  familyMembers: Array<FamilyMember>
  withQrCode: boolean
  open: boolean
  createMemorial: boolean
  imageId: string
  image: FileDTO
  creator: UserMinDTO
  createdAt: number
  updatedAt: number
  top: boolean // 置顶
  hide: boolean // 隐藏
  stat: ObituaryStat
  endPhraseIdx: number
  pureImage: FileDTO
  noticeDate: string
  watermarkImageId: string
  watermarkImage: FileDTO // 加水印的图片
  mapFileId: string //
  mapFile: FileDTO // 地图图片
  paid: boolean // 是否已经支付
}

export interface ObituaryStat {
  obituaryId: string
  pageView: number // pv
  wreathTotal: number // 花圈数量
  coupletTotal: number // 挽联数量
}

// 被赠送的花圈
export interface WreathSentDTO {
  id: string
  wreathTemplateId: string
  wreathTemplate: WreathTemplateDTO
  obituaryId: string
  senderId: string
  sender: UserMinDTO
  thanked: boolean
  price: number
  upperCouplet: string
  lowerCouplet: string
  senderSignature: string //真实姓名
  paid: boolean
  createdAt: number
  updatedAt: number
}

// 被赠送的挽联
export interface CoupletSentDTO {
  id: string
  templateId: string
  upper: string
  lower: string
  custom: boolean
  font: string
  imageId: string
  image: FileDTO
  obituaryId: string
  sender: string
  senderSignature: string
  senderUser: UserMinDTO
  thanked: boolean
  createdAt: number
  updatedAt: number
}

//
export interface ObituaryHistoryDTO {
  obituaryId: string
  fullName: string
  visitTime: number
  creatorId: string
}

export interface CreateWreathOrderCommand {
  templateId: string
  obituaryId: string
  custom: boolean
  upper: string
  lower: string
  signature: string
  payChannel: string
  inMiniApp: boolean
}

export function createWreathOrderNative(wreathOrderCommand: CreateWreathOrderCommand) {
  return netClient.request<CommonResponse<CreateOrderNativeResponse>>(
    OBITUARY_CREATE_WREATH_ORDER_NATIVE,
    'post',
    wreathOrderCommand
  )
}

export function createWreathOrderJS(wreathOrderCommand: CreateWreathOrderCommand) {
  return netClient.request<CommonResponse<CreateWechatOrderResponse>>(
    OBITUARY_CREATE_WREATH_ORDER_JS,
    'post',
    wreathOrderCommand
  )
}

// 挽联订单创建
export interface CreateCoupletOrderCommand {
  templateId: string
  obituaryId: string
  custom: boolean
  upper: string
  lower: string
  font: string
  fileId: string
  signature: string
  payChannel: string
  inMiniApp: boolean
}

// response

export function createCoupletOrderNative(coupletOrderCommand: CreateCoupletOrderCommand) {
  return netClient.request<CommonResponse<CreateOrderNativeResponse>>(
    OBITUARY_CREATE_COUPLET_ORDER_NATIVE,
    'post',
    coupletOrderCommand
  )
}

export function createCoupletOrderJS(coupletOrderCommand: CreateCoupletOrderCommand) {
  return netClient.request<CommonResponse<CreateWechatOrderResponse>>(
    OBITUARY_CREATE_COUPLET_ORDER_JS,
    'post',
    coupletOrderCommand
  )
}

export interface ListWreathSentResponse {
  id: string
  pageSize: number // echo每页个数
  total: number // 花圈总数
  items: Array<WreathSentDTO> // 本页数据
}
export function listWreathSent(obituaryId: string, lastWreathId: string) {
  return netClient.request<CommonResponse<ListWreathSentResponse>>(
    OBITUARY_LIST_WREATH_SENT.replace('{obituaryId}', obituaryId),
    'get',
    { pageSize: 15, lastWreathId: lastWreathId }
  )
}

export interface ListCoupletSentResponse {
  id: string
  pageSize: number
  total: number
  items: Array<CoupletSentDTO>
}

export function listCoupletSent(obituaryId: string, lastCoupletId: string) {
  return netClient.request<CommonResponse<ListCoupletSentResponse>>(
    OBITUARY_LIST_COUPLET_SENT.replace('{obituaryId}', obituaryId),
    'get',
    { pageSize: 10, lastCoupletId: lastCoupletId }
  )
}

export interface ReportPvCommand {
  url: string
  userId: string
}

export function obituaryReport(reportCommand: ReportPvCommand) {
  return netClient.request<CommonResponse<string>>(REPORT_PV, 'post', reportCommand)
}

export interface WreathThanksCommand {
  wreathId: string
  thanksMsg: string
  onOther: boolean
  otherName: string
}

export function wreathThanks(thanksCommand: WreathThanksCommand) {
  return netClient.request<CommonResponse<string>>(OBITUARY_WREATH_THANKS, 'post', thanksCommand)
}

export interface CoupletThanksCommand {
  coupletId: string
  thanksMsg: string
  onOther: boolean
  otherName: string
}

export function coupletThanks(thanksCommand: CoupletThanksCommand) {
  return netClient.request<CommonResponse<string>>(OBITUARY_COUPLET_THANKS, 'post', thanksCommand)
}

export interface ThanksListByItemIdCommand {
  itemId: string
}

export interface ObituaryThanksDTO {
  id: string
  wreathId: string
  coupletId: string
  obituaryId: string
  thanksMsg: string // 答谢信息
  onOther: boolean //
  otherName: string
  thanksName: string
  thanksTo: string
  thankBy: string
  thankByUser: UserMinDTO
  createdAt: number
}

export function thanksListByItemId(id: string, type: string) {
  if (type === 'couplet') {
    return netClient.request<CommonResponse<ObituaryThanksDTO>>(OBITUARY_THANKS_LIST_BY_ItemID, 'get', {
      coupletSentId: id
    })
  } else {
    return netClient.request<CommonResponse<ObituaryThanksDTO>>(OBITUARY_THANKS_LIST_BY_ItemID, 'get', {
      wreathSentId: id
    })
  }
}

export function thanksListByThanksId(thanksId: string) {
  return netClient.request<CommonResponse<ObituaryThanksDTO>>(
    OBITUARY_THANKS_LIST_BY_THANKSID.replace('{thanksId}', thanksId),
    'get',
    {}
  )
}

export interface ObituaryListResponse {
  obituaries: Array<ObituaryDTO>
  livesWithoutObituary: Array<LifeDTO>
  histories: Array<ObituaryHistoryDTO>
  total: number
}

export function obituaryList() {
  return netClient.request<CommonResponse<ObituaryListResponse>>(OBITUARY_LIST, 'get', {})
}

export interface DeleteWreathCommand {
  wreathSentId: string
}

export function deleteWreath(deleteCommand: DeleteWreathCommand) {
  return netClient.request<CommonResponse<string>>(OBITUARY_DELETE_WREATH, 'post', deleteCommand)
}

export interface DeleteCoupletCommand {
  coupletSentId: string
}

export function deleteCouplet(deleteCommand: DeleteCoupletCommand) {
  return netClient.request<CommonResponse<string>>(OBITUARY_DELETE_COUPLET, 'post', deleteCommand)
}

export interface SetLifePrivilegeCommand {
  obituaryId: string
}

export function setLifePrivilege(privilegeCommand: SetLifePrivilegeCommand) {
  return netClient.request<CommonResponse<boolean>>(OBITUARY_SET_LIFE_PRIVILEGE, 'post', privilegeCommand)
}

//花圈挽联样式本地缓存

export const wreathStyleCache = ref<WreathTemplateDTO[]>([])

export const coupletStyleCache = ref<CoupletTemplateDTO[]>([])
export const coupletCategoryCache = useLocalStorage<String[]>('CoupletCategoryCache', [])

export interface CreateLifeForObituaryCommand {
  obituaryId: string
}

export function createLifeByObituary(CreateLifeForObituaryCommand: CreateLifeForObituaryCommand) {
  return netClient.request<CommonResponse<LifeDTO>>(OBITUARY_CREATE_LIFE, 'post', CreateLifeForObituaryCommand)
}

export function checkSubscribeAll() {
  return netClient.request<CommonResponse<boolean>>(TEST_CHECK_SUBSCRIBE_ALL, 'post', {})
}

export const previewImgList: Ref<ObiPreviewDTO[]> = useUserLocalStorage('previewImgList', () => [])

export interface ObituaryPageResponse {
  total: number
  countWeekly: number
  items: Array<ObituaryDTO>
  page: number
  pageSize: number
}

export function obituaryOpenList(page: number, pageSize: number, sort: string) {
  return netClient.request<CommonResponse<ObituaryPageResponse>>(OBITUARY_OPEN_LIST, 'get', { page, pageSize, sort })
}

// request
export interface CreateObituaryOrderCommand {
  obituaryId: string
  payChannel: string
  inMiniApp: boolean
}

export function createObituaryOrderNative(obituaryOrderCommand: CreateObituaryOrderCommand) {
  return netClient.request<CommonResponse<CreateOrderNativeResponse>>(
    OBITUARY_CREATE_ORDER_NATIVE,
    'post',
    obituaryOrderCommand
  )
}

export function createObituaryOrderJS(obituaryOrderCommand: CreateObituaryOrderCommand) {
  return netClient.request<CommonResponse<CreateWechatOrderResponse>>(
    OBITUARY_CREATE_ORDER_JS,
    'post',
    obituaryOrderCommand
  )
}

export const showImage = (obituary: ObituaryDTO) => {
  if (obituary.paid) {
    return obituary.pureImage
      ? obituary.pureImage.url
      : obituary.image.url
        ? obituary.image.url
        : obituary.watermarkImage.url
  } else {
    return obituary.watermarkImage ? obituary.watermarkImage.url : obituary.image.url
  }
}

export interface DownloadImageFromUrlCommand {
  imageUrl: string
}

export function downloadImageFromUrl(downloadCommand: DownloadImageFromUrlCommand) {
  return netClient.request<CommonResponse<FileDTO>>(MEDIA_UPLOAD_IMAGE_BY_URL, 'post', downloadCommand)
}

//  request
export interface SetMemberObituaryPaidCommand {
  obituaryId: string
}

export function setMemberObituaryPaid(paidCommand: SetMemberObituaryPaidCommand) {
  return netClient.request<CommonResponse<boolean>>(SET_MEMBER_PAID, 'post', paidCommand)
}

export interface OpenObituaryResponse {
  currentStart: number
  currentEnd: number
  periodStart: number // 总的时间开始 - date like "2025-01-25"
  periodEnd: number // 总的时间结束 - date like "2026-03-24"
  openObituaries: Array<ObituaryDTO>
}
//GET /v1/obituary/open/period?startDate=2025-01-01&endDate=2025-01-31
export function getOpenObituaryPeriod(startDate: string, endDate: string) {
  return netClient.request<CommonResponse<OpenObituaryResponse>>(OBITUAYR_OPEN, 'get', { startDate, endDate })
}
